import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname.includes('-staging') 
    ? 'https://signalcue-api-staging-790924719842.asia-southeast1.run.app' 
    : 'https://signalcue-api-790924719842.asia-southeast1.run.app');

// Common recruitment sectors
const COMMON_SECTORS = [
  'Technology',
  'Finance',
  'Healthcare',
  'Engineering',
  'Sales',
  'Marketing',
  'Operations',
  'Human Resources',
  'Legal',
  'Education',
  'Manufacturing',
  'Retail',
  'Hospitality',
  'Construction',
  'Real Estate',
];

// Note: Data residency regions are now auto-detected based on country selection

// Agency size options
const AGENCY_SIZES = [
  { value: '1-10', label: '1-10 users' },
  { value: '11-50', label: '11-50 users' },
  { value: '51-200', label: '51-200 users' },
  { value: '201-500', label: '201-500 users' },
  { value: '500+', label: '500+ users' },
];

export default function SignUpForm({ onClose }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Organisation Details
    organisation_name: '',
    organisation_website: '',
    country: '',
    agency_size: '',
    recruitment_sectors: [],
    custom_sector: '',
    
    // Administrator User Details
    admin_full_name: '',
    admin_job_title: '',
    admin_email: '',
    
    // Compliance
    accept_terms: false,
    accept_privacy: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSectorToggle = (sector) => {
    setFormData(prev => {
      const sectors = [...prev.recruitment_sectors];
      const index = sectors.indexOf(sector);
      if (index > -1) {
        sectors.splice(index, 1);
      } else {
        sectors.push(sector);
      }
      return { ...prev, recruitment_sectors: sectors };
    });
  };

  const handleAddCustomSector = () => {
    if (formData.custom_sector.trim() && !formData.recruitment_sectors.includes(formData.custom_sector.trim())) {
      setFormData(prev => ({
        ...prev,
        recruitment_sectors: [...prev.recruitment_sectors, prev.custom_sector.trim()],
        custom_sector: '',
      }));
    }
  };

  const validateWebsite = async (url) => {
    try {
      // Normalize URL - add https:// if missing
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      
      // Basic URL format validation
      try {
        new URL(normalizedUrl);
      } catch {
        return 'Please enter a valid website URL (e.g., https://www.example.com)';
      }
      
      // Try to fetch the website to verify it exists
      const response = await fetch(normalizedUrl, {
        method: 'HEAD',
        mode: 'no-cors', // Use no-cors to avoid CORS issues, but we can't read response
      });
      
      // With no-cors, we can't check the response, but we can at least validate the URL format
      // For a more robust check, we could use a backend endpoint
      return null; // URL format is valid
    } catch (error) {
      return 'Please enter a valid website URL';
    }
  };

  const validateSectorSpelling = (sector) => {
    // Normalize the sector name for comparison
    const normalized = sector.trim().toLowerCase();
    
    // Check against common sectors (case-insensitive)
    const commonSectorsLower = COMMON_SECTORS.map(s => s.toLowerCase());
    
    // Check for exact match
    if (commonSectorsLower.includes(normalized)) {
      return null; // Valid
    }
    
    // Check for close matches (fuzzy matching for typos)
    const closeMatches = commonSectorsLower.filter(common => {
      // Check if sector is contained in common or vice versa
      if (normalized.includes(common) || common.includes(normalized)) {
        return true;
      }
      // Simple Levenshtein-like check for close spelling
      const diff = Math.abs(normalized.length - common.length);
      if (diff <= 2 && normalized.length > 3) {
        // Check character similarity
        let matches = 0;
        const minLen = Math.min(normalized.length, common.length);
        for (let i = 0; i < minLen; i++) {
          if (normalized[i] === common[i]) matches++;
        }
        if (matches / minLen > 0.7) {
          return true;
        }
      }
      return false;
    });
    
    if (closeMatches.length > 0) {
      const suggested = COMMON_SECTORS.find(s => 
        closeMatches.includes(s.toLowerCase())
      );
      return `Did you mean "${suggested}"? Please check the spelling.`;
    }
    
    // If it's a custom sector and doesn't match, allow it but warn
    return null; // Allow custom sectors, but could add a warning
  };

  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return 'Email address is required';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    
    // Additional validation: check for common typos
    const emailLower = email.toLowerCase();
    const commonDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'company.com'];
    const domain = emailLower.split('@')[1];
    
    if (domain) {
      // Check for common typos in domain
      const typos = {
        'gmial.com': 'gmail.com',
        'gmai.com': 'gmail.com',
        'gmal.com': 'gmail.com',
        'yahooo.com': 'yahoo.com',
        'yaho.com': 'yahoo.com',
      };
      
      if (typos[domain]) {
        return `Did you mean ${email.split('@')[0]}@${typos[domain]}?`;
      }
    }
    
    return null;
  };

  const validateStep = async (step) => {
    if (step === 1) {
      if (!formData.organisation_name || !formData.organisation_name.trim()) {
        return 'Organisation name is required';
      }
      if (!formData.organisation_website || !formData.organisation_website.trim()) {
        return 'Company website is required';
      }
      // Validate website
      const websiteError = await validateWebsite(formData.organisation_website);
      if (websiteError) {
        return websiteError;
      }
      if (!formData.country) {
        return 'Country is required';
      }
      if (!formData.agency_size) {
        return 'Agency size is required';
      }
      if (!formData.recruitment_sectors || formData.recruitment_sectors.length === 0) {
        return 'Please select at least one recruitment sector';
      }
      // Validate sector spelling
      for (const sector of formData.recruitment_sectors) {
        const sectorError = validateSectorSpelling(sector);
        if (sectorError) {
          return sectorError;
        }
      }
    } else if (step === 2) {
      if (!formData.admin_full_name || !formData.admin_full_name.trim()) {
        return 'Full name is required';
      }
      if (!formData.admin_job_title || !formData.admin_job_title.trim()) {
        return 'Job title is required';
      }
      const emailError = validateEmail(formData.admin_email);
      if (emailError) {
        return emailError;
      }
    } else if (step === 3) {
      if (!formData.accept_terms) {
        return 'You must accept the Terms of Service';
      }
      if (!formData.accept_privacy) {
        return 'You must accept the Privacy Policy';
      }
    }
    return null;
  };

  const handleNext = async () => {
    setError('');
    setLoading(true);
    try {
      const validationError = await validateStep(currentStep);
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }
      setError('');
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } catch (err) {
      setError(err.message || 'Validation error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const validationError = await validateStep(3);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Prepare sectors list (combine selected and custom)
      const allSectors = [...formData.recruitment_sectors];
      
      // Validate that at least one sector is selected
      if (allSectors.length === 0) {
        setError('Please select at least one recruitment sector');
        setLoading(false);
        return;
      }

      const payload = {
        organisation_name: formData.organisation_name,
        organisation_website: formData.organisation_website,
        country: formData.country,
        agency_size: formData.agency_size,
        recruitment_sectors: allSectors,
        admin_full_name: formData.admin_full_name,
        admin_job_title: formData.admin_job_title,
        admin_email: formData.admin_email,
        accept_terms: formData.accept_terms,
        accept_privacy: formData.accept_privacy,
      };

      console.log('Submitting signup:', payload);

      const response = await fetch(`${API_URL}/api/public/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to submit sign-up form');
      }

      console.log('Signup successful:', responseData);
      setSuccess(true);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = '#00C6AE';
  const primaryDark = '#000000';
  const textPrimary = '#FFFFFF';
  const textSecondary = '#B0B0B0';
  const borderColor = '#1A1A1A';
  const cardBg = '#111111';

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
    overflowY: 'auto',
  };

  const modalStyle = {
    background: cardBg,
    border: `1px solid ${borderColor}`,
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '600px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 8px 40px rgba(0, 232, 218, 0.15)',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'transparent',
    border: 'none',
    color: textSecondary,
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: primaryDark,
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    color: textPrimary,
    fontSize: '15px',
    marginBottom: '16px',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    color: textSecondary,
    fontSize: '14px',
    fontWeight: 500,
  };

  const buttonStyle = {
    padding: '12px 24px',
    background: `linear-gradient(135deg, ${primaryColor}, #00B8D9)`,
    color: primaryDark,
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };

  const stepIndicatorStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '32px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${borderColor}`,
  };

  const stepStyle = {
    flex: 1,
    textAlign: 'center',
    padding: '8px',
    color: textSecondary,
    fontSize: '14px',
  };

  const stepActiveStyle = {
    ...stepStyle,
    color: primaryColor,
    fontWeight: 600,
  };

  if (success) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <button style={closeButtonStyle} onClick={onClose}>×</button>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', color: primaryColor }}>✓</div>
            <h2 style={{ color: primaryColor, marginBottom: '12px' }}>Account Created!</h2>
            <p style={{ color: textSecondary, marginBottom: '8px' }}>
              We've sent a verification email to <strong style={{ color: textPrimary }}>{formData.admin_email}</strong>
            </p>
            <p style={{ color: textSecondary, fontSize: '14px' }}>
              Please check your email and click the verification link to activate your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>×</button>
        
        <div style={{ marginBottom: '24px' }}>
          <img src="/sc_dark.png" alt="SignalCue" style={{ height: '32px', width: 'auto', marginBottom: '16px' }} />
          <h2 style={{ color: textPrimary, fontSize: '24px', marginBottom: '8px' }}>Create Your Account</h2>
          <p style={{ color: textSecondary, fontSize: '14px' }}>Step {currentStep} of {totalSteps}</p>
        </div>

        {/* Step Indicator */}
        <div style={stepIndicatorStyle}>
          <div style={currentStep >= 1 ? stepActiveStyle : stepStyle}>1. Organisation</div>
          <div style={currentStep >= 2 ? stepActiveStyle : stepStyle}>2. Administrator</div>
          <div style={currentStep >= 3 ? stepActiveStyle : stepStyle}>3. Compliance</div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Organisation Details */}
          {currentStep === 1 && (
            <div>
              <div>
                <label htmlFor="organisation_name" style={labelStyle}>Recruitment Company Name *</label>
                <input
                  id="organisation_name"
                  name="organisation_name"
                  type="text"
                  value={formData.organisation_name}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="Your Recruitment Company"
                />
              </div>

              <div>
                <label htmlFor="organisation_website" style={labelStyle}>Company Website *</label>
                <input
                  id="organisation_website"
                  name="organisation_website"
                  type="url"
                  value={formData.organisation_website}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="https://www.example.com"
                />
                <p style={{ color: textSecondary, fontSize: '12px', marginTop: '-12px', marginBottom: '16px' }}>
                  Your company's website URL
                </p>
              </div>

              <div>
                <label htmlFor="country" style={labelStyle}>Country of Operation *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                >
                  <option value="">Select a country</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Singapore">Singapore</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Indonesia">Indonesia</option>
                  <option value="Vietnam">Vietnam</option>
                  <option value="Philippines">Philippines</option>
                  <option value="Hong Kong">Hong Kong</option>
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Other">Other</option>
                </select>
                <p style={{ color: textSecondary, fontSize: '12px', marginTop: '-12px', marginBottom: '16px' }}>
                  Timezone and data residency will be automatically set based on your country selection
                </p>
              </div>

              <div>
                <label htmlFor="agency_size" style={labelStyle}>Approximate Agency Size *</label>
                <select
                  id="agency_size"
                  name="agency_size"
                  value={formData.agency_size}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                >
                  <option value="">Select size</option>
                  {AGENCY_SIZES.map(size => (
                    <option key={size.value} value={size.value}>{size.label}</option>
                  ))}
                </select>
                <p style={{ color: textSecondary, fontSize: '12px', marginTop: '-12px', marginBottom: '16px' }}>
                  Select the approximate number of users in your agency
                </p>
              </div>

              <div>
                <label style={labelStyle}>Primary Recruitment Sectors *</label>
                <p style={{ color: textSecondary, fontSize: '12px', marginBottom: '12px' }}>
                  Select all sectors your agency recruits for (you can add custom sectors below)
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px' }}>
                  {COMMON_SECTORS.map(sector => (
                    <label key={sector} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.recruitment_sectors.includes(sector)}
                        onChange={() => handleSectorToggle(sector)}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      <span style={{ color: textPrimary, fontSize: '14px' }}>{sector}</span>
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={formData.custom_sector}
                    onChange={(e) => setFormData({ ...formData, custom_sector: e.target.value })}
                    placeholder="Add custom sector"
                    style={{ ...inputStyle, marginBottom: '0', flex: 1 }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomSector();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomSector}
                    style={{
                      ...buttonStyle,
                      padding: '12px 16px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Add
                  </button>
                </div>
                {formData.recruitment_sectors.length > 0 && (
                  <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {formData.recruitment_sectors.map(sector => (
                      <span
                        key={sector}
                        style={{
                          background: primaryColor + '20',
                          color: primaryColor,
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {sector}
                        <button
                          type="button"
                          onClick={() => handleSectorToggle(sector)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: primaryColor,
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: 0,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Administrator Details */}
          {currentStep === 2 && (
            <div>
              <div>
                <label htmlFor="admin_full_name" style={labelStyle}>Full Name *</label>
                <input
                  id="admin_full_name"
                  name="admin_full_name"
                  type="text"
                  value={formData.admin_full_name}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="admin_job_title" style={labelStyle}>Job Title *</label>
                <input
                  id="admin_job_title"
                  name="admin_job_title"
                  type="text"
                  value={formData.admin_job_title}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="e.g., Managing Director, CEO, Operations Manager"
                />
              </div>

              <div>
                <label htmlFor="admin_email" style={labelStyle}>Work Email Address *</label>
                <input
                  id="admin_email"
                  name="admin_email"
                  type="email"
                  value={formData.admin_email}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="you@company.com"
                />
                <p style={{ color: textSecondary, fontSize: '12px', marginTop: '-12px', marginBottom: '16px' }}>
                  This will be your administrator account email
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Compliance */}
          {currentStep === 3 && (
            <div>
              <div style={{ 
                background: 'rgba(0, 198, 174, 0.1)', 
                border: `1px solid ${primaryColor}40`, 
                borderRadius: '8px', 
                padding: '16px', 
                marginBottom: '24px' 
              }}>
                <p style={{ color: textPrimary, fontSize: '14px', marginBottom: '12px', fontWeight: 500 }}>
                  Please review our policies before proceeding:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open('/terms', '_blank');
                    }}
                    style={{ 
                      color: primaryColor, 
                      textDecoration: 'underline',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    View Terms of Service →
                  </a>
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open('/privacy', '_blank');
                    }}
                    style={{ 
                      color: primaryColor, 
                      textDecoration: 'underline',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    View Privacy Policy →
                  </a>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '16px' }}>
                  <input
                    type="checkbox"
                    name="accept_terms"
                    checked={formData.accept_terms}
                    onChange={handleChange}
                    required
                    style={{ marginRight: '12px', marginTop: '4px', cursor: 'pointer', minWidth: '18px' }}
                  />
                  <span style={{ color: textPrimary, fontSize: '14px', lineHeight: '1.5' }}>
                    I accept the <a 
                      href="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('/terms', '_blank');
                      }}
                      style={{ color: primaryColor, textDecoration: 'underline' }}
                    >Terms of Service</a> *
                  </span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="accept_privacy"
                    checked={formData.accept_privacy}
                    onChange={handleChange}
                    required
                    style={{ marginRight: '12px', marginTop: '4px', cursor: 'pointer', minWidth: '18px' }}
                  />
                  <span style={{ color: textPrimary, fontSize: '14px', lineHeight: '1.5' }}>
                    I acknowledge the <a 
                      href="/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('/privacy', '_blank');
                      }}
                      style={{ color: primaryColor, textDecoration: 'underline' }}
                    >Privacy Policy</a> *
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                style={{
                  ...buttonStyle,
                  background: 'transparent',
                  border: `1px solid ${borderColor}`,
                  color: textPrimary,
                  flex: 1,
                }}
              >
                Back
              </button>
            )}
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                style={{
                  ...buttonStyle,
                  flex: currentStep === 1 ? 1 : 1,
                }}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
